import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from "react";
import { z } from "zod";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

/* ─── Zod Schemas ─── */
const cartItemSchema = z.object({
  id: z.string().uuid(),
  name: z.string().min(1),
  price: z.number().positive(),
  originalPrice: z.number().positive(),
  quantity: z.number().int().min(1).max(99),
  emoji: z.string(),
  customText: z.string().optional(),
  imageUrl: z.string().optional(),
  vendorId: z.string().optional(),
  vendorName: z.string().optional(),
});

export type CartItem = z.infer<typeof cartItemSchema>;

const cartSchema = z.array(cartItemSchema);

/* ─── Shipping Options ─── */
export const shippingOptions = [
  { id: "express", label: "משלוח מהיר (2-3 ימים)", price: 39 },
  { id: "pickup", label: "איסוף עצמי", price: 0 },
] as const;

export type ShippingOptionId = (typeof shippingOptions)[number]["id"];

/* ─── Context ─── */
interface CartContextType {
  items: CartItem[];
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  addItem: (item: Omit<CartItem, "quantity">, qty?: number) => void;
  removeItem: (id: string) => void;
  updateQuantity: (id: string, qty: number) => void;
  clearCart: () => void;
  loadItems: (items: CartItem[]) => void;
  itemCount: number;
  subtotal: number;
  isValid: boolean;
  vendorShipping: Record<string, ShippingOptionId>;
  setVendorShipping: (vendorId: string, optionId: ShippingOptionId) => void;
  shippingTotal: number;
  grandTotal: number;
  activeSharedCart: { shareCode: string; cartId: string } | null;
  clearActiveSharedCart: () => void;
}

const CartContext = createContext<CartContextType>({
  items: [],
  isOpen: false,
  setIsOpen: () => {},
  addItem: () => {},
  removeItem: () => {},
  updateQuantity: () => {},
  clearCart: () => {},
  loadItems: () => {},
  itemCount: 0,
  subtotal: 0,
  isValid: true,
  vendorShipping: {},
  setVendorShipping: () => {},
  shippingTotal: 0,
  grandTotal: 0,
  activeSharedCart: null,
  clearActiveSharedCart: () => {},
});

export const useCart = () => useContext(CartContext);

const STORAGE_KEY = "smileyland-cart";
const SHIPPING_KEY = "smileyland-shipping";

export const CartProvider = ({ children }: { children: ReactNode }) => {
  const [items, setItems] = useState<CartItem[]>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) return [];
      const parsed = JSON.parse(stored);
      const result = cartSchema.safeParse(parsed);
      return result.success ? result.data : [];
    } catch {
      return [];
    }
  });
  const [isOpen, setIsOpen] = useState(false);
  const [vendorShipping, setVendorShippingState] = useState<Record<string, ShippingOptionId>>(() => {
    try {
      const stored = localStorage.getItem(SHIPPING_KEY);
      return stored ? JSON.parse(stored) : {};
    } catch {
      return {};
    }
  });

  // Persist to localStorage
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  useEffect(() => {
    localStorage.setItem(SHIPPING_KEY, JSON.stringify(vendorShipping));
  }, [vendorShipping]);

  // Active shared cart
  const getActiveSharedCart = useCallback(() => {
    try {
      const stored = localStorage.getItem("smileyland-active-shared-cart");
      return stored ? JSON.parse(stored) : null;
    } catch { return null; }
  }, []);

  const clearActiveSharedCart = useCallback(() => {
    localStorage.removeItem("smileyland-active-shared-cart");
  }, []);

  const addItem = useCallback(
    async (item: Omit<CartItem, "quantity">, qty = 1) => {
      const validation = cartItemSchema.safeParse({ ...item, quantity: qty });
      if (!validation.success) {
        toast.error("שגיאה בהוספת המוצר לסל");
        return;
      }

      // Check if there's an active shared cart
      const sharedCart = getActiveSharedCart();
      if (sharedCart?.cartId) {
        try {
          const { data: cartRows } = await (supabase as any).rpc("get_shared_cart", {
            _share_code: sharedCart.shareCode,
          });
          const cartData = Array.isArray(cartRows) ? cartRows[0] : cartRows;

          if (cartData) {
            const existingItems = (cartData.items as any[]) || [];
            const newItem = {
              ...item,
              quantity: qty,
              addedBy: sharedCart.userId,
              addedByName: sharedCart.userName,
            };
            const existing = existingItems.find(
              (i: any) => i.id === item.id && i.addedBy === sharedCart.userId
            );
            let updatedItems;
            if (existing) {
              updatedItems = existingItems.map((i: any) =>
                i.id === item.id && i.addedBy === sharedCart.userId
                  ? { ...i, quantity: Math.min(i.quantity + qty, 99) }
                  : i
              );
            } else {
              updatedItems = [...existingItems, newItem];
            }

            await (supabase as any).rpc("update_shared_cart_items", {
              _cart_id: sharedCart.cartId,
              _items: updatedItems,
            });

            toast.success(`${item.name} נוסף לסל המשותף! 🛒`, {
              action: {
                label: "חזרה לסל",
                onClick: () => window.location.href = `/סל-משותף/${sharedCart.shareCode}`,
              },
            });
            return;
          }
        } catch {
          // Fall through to regular cart
        }
      }

      setItems((prev) => {
        const existing = prev.find((i) => i.id === item.id && i.customText === item.customText);
        if (existing) {
          const newQty = Math.min(existing.quantity + qty, 99);
          return prev.map((i) =>
            i.id === item.id && i.customText === item.customText
              ? { ...i, quantity: newQty }
              : i
          );
        }
        return [...prev, { ...item, quantity: qty }];
      });

      // Auto-set default shipping for new vendors
      const vendorKey = item.vendorId || "_platform";
      setVendorShippingState((prev) => {
        if (!prev[vendorKey]) {
          return { ...prev, [vendorKey]: "express" as ShippingOptionId };
        }
        return prev;
      });

      toast.success(`${item.name} נוסף לסל!`);
      setIsOpen(true);
    },
    [getActiveSharedCart]
  );

  const removeItem = useCallback((id: string) => {
    setItems((prev) => prev.filter((i) => i.id !== id));
  }, []);

  const updateQuantity = useCallback((id: string, qty: number) => {
    if (qty < 1) {
      removeItem(id);
      return;
    }
    const qtyValidation = z.number().int().min(1).max(99).safeParse(qty);
    if (!qtyValidation.success) return;

    setItems((prev) =>
      prev.map((i) => (i.id === id ? { ...i, quantity: qty } : i))
    );
  }, [removeItem]);

  const clearCart = useCallback(() => {
    setItems([]);
    setVendorShippingState({});
  }, []);

  const loadItems = useCallback((newItems: CartItem[]) => {
    const result = cartSchema.safeParse(newItems);
    if (result.success) {
      setItems(result.data);
      toast.success("הסל נטען בהצלחה!");
    } else {
      toast.error("שגיאה בטעינת הסל");
    }
  }, []);

  const setVendorShipping = useCallback((vendorId: string, optionId: ShippingOptionId) => {
    setVendorShippingState((prev) => ({ ...prev, [vendorId]: optionId }));
  }, []);

  const itemCount = items.reduce((sum, i) => sum + i.quantity, 0);
  const subtotal = items.reduce((sum, i) => sum + i.price * i.quantity, 0);
  const isValid = cartSchema.safeParse(items).success && subtotal >= 0;

  // Calculate shipping total from unique vendors
  const vendorIds = [...new Set(items.map((i) => i.vendorId || "_platform"))];
  const shippingTotal = vendorIds.reduce((sum, vid) => {
    const optionId = vendorShipping[vid] || "standard";
    const option = shippingOptions.find((o) => o.id === optionId);
    return sum + (option?.price ?? 0);
  }, 0);

  // Free shipping per vendor if vendor subtotal >= 500
  const vendorSubtotals = new Map<string, number>();
  items.forEach((i) => {
    const vid = i.vendorId || "_platform";
    vendorSubtotals.set(vid, (vendorSubtotals.get(vid) || 0) + i.price * i.quantity);
  });

  const effectiveShippingTotal = vendorIds.reduce((sum, vid) => {
    const vendorSub = vendorSubtotals.get(vid) || 0;
    if (vendorSub >= 199) return sum; // Free shipping
    const optionId = vendorShipping[vid] || "standard";
    const option = shippingOptions.find((o) => o.id === optionId);
    return sum + (option?.price ?? 0);
  }, 0);

  const grandTotal = subtotal + effectiveShippingTotal;

  return (
    <CartContext.Provider
      value={{
        items,
        isOpen,
        setIsOpen,
        addItem,
        removeItem,
        updateQuantity,
        clearCart,
        loadItems,
        itemCount,
        subtotal,
        isValid,
        vendorShipping,
        setVendorShipping,
        shippingTotal: effectiveShippingTotal,
        grandTotal,
        activeSharedCart: getActiveSharedCart(),
        clearActiveSharedCart,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};
